// Copyright (c) 2023 Park Hyunwoo
//
// This software is released under the MIT License.
// https://opensource.org/licenses/MIT

import { generateBandEqFrequencies } from '@/eq/bandeq'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const bandCountTable = [3, 5, 10, 16, 31, 60, 120]

export const useFrequencyStore = defineStore('frquency', () => {
  const bandCountIndex = ref(0)
  const currentBandCount = computed(() => bandCountTable[bandCountIndex.value])

  const currentBandIndex = ref(1)
  const currentLowerFreqIndex = ref(0)
  const currentUpperBandIndex = ref(currentBandCount.value - 1)
  const currentBandEqFrequencies = computed(() => generateBandEqFrequencies(currentBandCount.value))

  const Q = computed(() => {
    const freqRatio = Math.pow(1000, 1 / (currentBandCount.value - 1))
    return Math.sqrt(freqRatio) / (freqRatio - 1)
  })

  const frequency = computed(() => {
    return currentBandEqFrequencies.value[currentBandIndex.value]
  })

  const gain = computed(() => {
    return Math.pow(currentBandCount.value, 0.7)
  })

  function selectUpperBand (): void {
    currentBandIndex.value = Math.min(currentBandCount.value - 1, currentBandIndex.value + 1)
  }

  function selectLowerBand (): void {
    currentBandIndex.value = Math.max(0, currentBandIndex.value - 1)
  }

  function selectRandomBand (): void {
    currentBandIndex.value = (
      Math.floor(Math.random() * (currentUpperBandIndex.value - currentLowerFreqIndex.value + 1)) +
            currentLowerFreqIndex.value
    )
  }

  function doubleBandCount (): void {
    bandCountIndex.value = Math.min(bandCountIndex.value + 1, bandCountTable.length - 1)
    currentLowerFreqIndex.value = 0
    currentUpperBandIndex.value = currentBandCount.value - 1
    currentBandIndex.value = (currentBandCount.value / 2) | 0
  }

  function halveBandCount (): void {
    bandCountIndex.value = Math.max(bandCountIndex.value - 1, 0)
    currentLowerFreqIndex.value = 0
    currentUpperBandIndex.value = currentBandCount.value - 1
    currentBandIndex.value = (currentBandCount.value / 2) | 0
  }

  function setLowerFrequencyLimit (index: number): void {
    currentLowerFreqIndex.value = index
  }

  function setUpperFrequencyLimit (index: number): void {
    currentUpperBandIndex.value = index
  }

  return {
    doubleBandCount,
    halveBandCount,
    currentBandEqFrequencies,

    setLowerFrequencyLimit,
    setUpperFrequencyLimit,

    frequency,
    Q,
    gain,
    currentBandIndex,
    currentLowerFreqIndex,
    currentUpperBandIndex,

    selectUpperBand,
    selectLowerBand,
    selectRandomBand
  }
})
